// One salesman: what stands out against the team, their book, who to call and
// which products to push to their customers.
import { useMemo, useRef, useState } from "react";
import { getCustomerHealth, getProductOpportunities } from "../../distflowAPI/salesAnalyticsApi";
import CallSheet from "./CallSheet";
import DetailPanel, { PanelHead } from "./DetailPanel";
import { plural } from "./customerText";
import { count, money, pct, shortRange } from "./format";
import useSideReport from "./useSideReport";

const CALLS = 5;
const PUSH = 3;

/** Sentences about a salesman's book, the ones the scorecard flagged first. */
export function standsOut(row, medians = {}, comparisonDates = "") {
  const flags = new Set(row.flags || []);
  const team = (key) => (!row.is_office && medians[key] != null ? `; team median ${pct(medians[key], 0)}` : "");
  const items = [];
  if (row.book_accounts) {
    items.push({ flag: flags.has("low_coverage"),
      text: `${count(row.active_book_accounts)} of ${count(row.book_accounts)} accounts ordered (${pct(row.coverage_percent, 0)}${team("coverage_percent")}).` });
  }
  if (row.previous_book_customers) {
    items.push({ flag: flags.has("losing_customers"),
      text: `Kept ${count(row.retained_customers)} of ${count(row.previous_book_customers)} customers from ${comparisonDates} (${pct(row.retention_percent, 0)}${team("retention_percent")}).`
        + (row.lost_customers ? ` The ${count(row.lost_customers)} lost spent ${money(row.lost_customer_sales)} then.` : "") });
  }
  if (row.existing_customer_growth_percent != null) {
    const growth = Number(row.existing_customer_growth_percent);
    items.push({ flag: flags.has("shrinking_accounts"),
      text: `Customers they kept are spending ${pct(Math.abs(growth), 0)} ${growth >= 0 ? "more" : "less"}.` });
  }
  if (row.at_risk_customers) {
    items.push({ flag: flags.has("unattended_risk"),
      text: `${count(row.at_risk_customers)} ${plural(row.at_risk_customers, "customer needs", "customers need")} attention (${money(row.at_risk_weekly)} a week), ${count(row.at_risk_without_follow_up)} with no task.` });
  }
  if (!row.is_office) {
    if (flags.has("no_new_business")) items.push({ flag: true, text: "No new customers or accounts in these dates." });
    else if (row.new_customers || row.new_accounts) {
      items.push({ text: `${count(row.new_customers)} new ${plural(row.new_customers, "customer")}, ${count(row.new_accounts)} ${plural(row.new_accounts, "account")} opened.` });
    }
    if (row.tasks_logged != null) {
      items.push({ flag: flags.has("no_tasks"), text: row.tasks_logged
        ? `${count(row.tasks_logged)} ${plural(row.tasks_logged, "task")} logged, ${count(row.tasks_completed)} done${row.tasks_overdue ? `, ${row.tasks_overdue} overdue` : ""}.`
        : `No tasks logged in these dates${row.tasks_overdue ? `; ${row.tasks_overdue} overdue` : ""}.` });
    }
  }
  return [...items.filter((item) => item.flag), ...items.filter((item) => !item.flag)];
}

export default function SalesmanPanel({ mode, row, team, comparison, request, scope, onClose, onOpenCustomer, onOpenProduct, onFocus }) {
  const medians = team?.medians || {};
  const comparisonDates = comparison ? shortRange(comparison.start_date, comparison.end_date) : "";
  const headingRef = useRef(null);
  const [printJob, setPrintJob] = useState(0);
  const healthParams = useMemo(() => ({ as_of: request?.end_date, territory_id: request?.territory_id, filters: [row.key] }),
    [request?.end_date, request?.territory_id, row.key]);
  const productParams = useMemo(() => ({
    start_date: request?.start_date, end_date: request?.end_date, territory_id: request?.territory_id, comparison: request?.comparison,
    comparison_start_date: request?.comparison_start_date, comparison_end_date: request?.comparison_end_date, filters: [row.key],
  }), [request, row.key]);
  const health = useSideReport(getCustomerHealth, healthParams, Boolean(request?.end_date));
  const products = useSideReport(getProductOpportunities, productParams, Boolean(request?.start_date));
  const calls = health.data?.rows || [];
  const push = (products.data?.rows || []).filter((product) => Number(product.potential_weekly) > 0).slice(0, PUSH);
  const change = comparison && row.sales_change_percent != null
    ? `, ${Number(row.sales_change_percent) >= 0 ? "up" : "down"} ${pct(Math.abs(Number(row.sales_change_percent)))}` : "";
  const subtitle = row.is_office
    ? `${count(row.book_accounts)} accounts with no salesman · ${money(row.sales)} in sales${change}`
    : `${count(row.book_accounts)} ${plural(row.book_accounts, "account")} in their book · ${money(row.sales)} in sales${change}`;
  const notes = standsOut(row, medians, comparisonDates);

  return <DetailPanel mode={mode} label="Salesman details" busy={health.loading || products.loading} onClose={onClose}>
    <div className="sa-panel-body">
      <PanelHead title={row.name} subtitle={subtitle} onClose={onClose} closeLabel="Close salesman details" headingRef={headingRef} />

      {notes.length > 0 && <section className="sa-panel-section" aria-label="What stands out">
        <h3>What stands out</h3>
        <ul className="sa-panel-bullets">{notes.map((item) => <li key={item.text} className={item.flag ? "is-flagged" : ""}>{item.text}</li>)}</ul>
      </section>}

      <dl className="sa-panel-stats">
        <div><dt>Per account per week</dt><dd>{row.sales_per_account_weekly == null ? "—" : money(row.sales_per_account_weekly)}
          {!row.is_office && medians.sales_per_account_weekly != null && <small>median {money(medians.sales_per_account_weekly)}</small>}</dd></div>
        <div><dt>New business</dt><dd>{row.new_customers || row.new_accounts
          ? <>{count(row.new_customers)} {plural(row.new_customers, "customer")}<small>{count(row.new_accounts)} {plural(row.new_accounts, "account")} opened</small></> : "None"}</dd></div>
        <div><dt>Categories per customer</dt><dd>{row.categories_per_customer ?? "—"}
          {!row.is_office && medians.categories_per_customer != null && <small>median {Number(medians.categories_per_customer).toFixed(1)}</small>}</dd></div>
        <div><dt>On standing orders</dt><dd>{pct(row.standing_order_share_percent, 0)}<small>of their sales</small></dd></div>
      </dl>

      <section className="sa-panel-section" aria-label="Call list">
        <h3>{row.is_office ? "Call list" : "Their call list"}</h3>
        {health.loading ? <p className="sa-panel-muted">Checking their customers…</p>
          : health.failed ? <p className="sa-panel-muted">The call list could not be loaded.</p>
            : calls.length ? <ul className="sa-panel-list">{calls.slice(0, CALLS).map((customer) => {
              const reason = customer.reasons?.find((item) => item.type === customer.status) || customer.reasons?.[0];
              return <li key={customer.key}>
                <span><button type="button" className="sa-row-link" onClick={() => onOpenCustomer?.(customer.key)}>{customer.name}</button>
                  {" · "}{customer.status_label?.toLowerCase()}{reason ? `: ${reason.detail}` : ""}</span>
                <span>{money(customer.at_risk_weekly)}/wk</span>
              </li>;
            })}</ul> : <p>No regular customers of theirs are behind their usual pattern.</p>}
        {calls.length > CALLS && <p className="sa-panel-muted">+{calls.length - CALLS} more on the call sheet.</p>}
      </section>

      <section className="sa-panel-section" aria-label="Products to push">
        <h3>Push to their customers</h3>
        {products.loading ? <p className="sa-panel-muted">Looking at what their customers buy…</p>
          : push.length ? <ul className="sa-panel-list">{push.map((product) => {
            const type = (product.reach_by_account_type || []).find((item) => item.counted && item.non_buyers);
            return <li key={product.key}>
              <span><button type="button" className="sa-row-link" onClick={() => onOpenProduct?.(product.key)}>{product.name}</button>
                {type && ` · ${count(type.non_buyers)} of their ${type.account_type} customers don’t buy it`}</span>
              <span>up to {money(product.potential_weekly)}/wk</span>
            </li>;
          })}</ul> : <p>{products.failed ? "Products could not be loaded." : "Nothing stands out: no product is missing from many of their similar customers."}</p>}
      </section>

      <div className="sa-panel-actions">
        {calls.length > 0 && <button type="button" className="sa-outline-button" onClick={() => setPrintJob(printJob + 1)}>Print their call sheet</button>}
        <button type="button" onClick={() => onFocus?.(row)}>Show the whole page for {row.name}</button>
      </div>
      {printJob > 0 && <CallSheet key={printJob} title={`Needs attention · ${row.name}`} asOf={request?.end_date} scope={scope} rows={calls} />}
    </div>
  </DetailPanel>;
}
